import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { FileText, Plus, Send, Download, Trash2, CheckCircle, FileSignature } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const CATEGORIES = ['viticulture', 'phyto', 'mecanique', 'transport', 'communal', 'autre'];
const UNITES = ['ha', 'h', 'km', 'm3', 'forfait', 'passage', 'ml'];

const STATUT_BADGES = {
  brouillon: { label: 'Brouillon', cls: 'bg-gray-200 text-gray-700' },
  envoye:    { label: 'Envoyé',    cls: 'bg-amber-100 text-amber-800' },
  signe:     { label: 'Signé ✓',  cls: 'bg-emerald-100 text-emerald-800' },
  refuse:    { label: 'Refusé',    cls: 'bg-red-100 text-red-700' },
  archive:   { label: 'Archivé',   cls: 'bg-slate-100 text-slate-600' },
};

const nouveauFormulaire = () => ({
  client_id: '',
  client: { nom: '', prenom: '', siret: '', adresse: '', code_postal: '', ville: '', email: '', telephone: '' },
  titre: '',
  prestations: [{ categorie: 'viticulture', libelle: '', quantite: 1, unite: 'ha', prix_unitaire_ht: 0, remise_percent: 0, notes: '' }],
  conditions: {
    mode_paiement: 'virement', mode_facturation: 'intervention',
    delai_paiement_jours: 30, acompte_percent: 0,
    date_debut: '', date_fin: '', lieu_execution: '',
    force_majeure: true, juridiction: 'Tribunal de commerce compétent',
    clauses_specifiques: [],
  },
  notes_internes: '',
});

export default function ContratsETASimplePage() {
  const [contrats, setContrats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(nouveauFormulaire());
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [showEnvoi, setShowEnvoi] = useState(null);
  const [messageEnvoi, setMessageEnvoi] = useState('');
  const [clients, setClients] = useState([]);

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/contrats-eta/contrats');
      setContrats(r.data.contrats || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { charger(); }, [charger]);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/crm/contacts?limit=500');
        setClients(r.data.contacts || r.data || []);
      } catch { /* silent */ }
    })();
  }, []);

  // Pré-remplir le formulaire si arrivée depuis la fiche CRM d'une entreprise
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const clientId = params.get('client_id');
      const clientNom = params.get('client_nom');
      if (clientId && clientNom && !showForm) {
        setForm(prev => ({
          ...nouveauFormulaire(),
          client_id: clientId,
          client: { ...nouveauFormulaire().client, nom: decodeURIComponent(clientNom) },
        }));
        setShowForm(true);
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async () => {
    if (!form.client.nom || form.prestations.length === 0) {
      toast.error('Client et au moins 1 prestation requis'); return;
    }
    try {
      if (editId) {
        await api.put(`/contrats-eta/v2/${editId}`, form);
        toast.success('Contrat mis à jour');
      } else {
        const r = await api.post('/contrats-eta/v2', form);
        toast.success(`Contrat ${r.data.contrat.numero} créé`);
      }
      setShowForm(false); setEditId(null); setForm(nouveauFormulaire());
      await charger();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const editer = (c) => {
    setForm({
      client_id: c.client_id || '',
      client: c.client || {},
      titre: c.titre || '',
      prestations: c.prestations_v2 || [{ categorie: 'viticulture', libelle: '', quantite: 1, unite: 'ha', prix_unitaire_ht: 0, remise_percent: 0 }],
      conditions: c.conditions_v2 || nouveauFormulaire().conditions,
      notes_internes: c.notes_internes || '',
    });
    setEditId(c.id); setShowForm(true);
  };

  const supprimer = async (id) => {
    if (!window.confirm('Supprimer ce contrat ?')) return;
    try { await api.delete(`/contrats-eta/v2/${id}`); toast.success('Supprimé'); await charger(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const telecharger = (id, numero) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('token');
    const sid = localStorage.getItem('active_societe_id') || '';
    fetch(`${base}/api/contrats-eta/v2/${id}/pdf`, {
      headers: { Authorization: `Bearer ${token}`, 'X-Societe-Id': sid },
    }).then(r => r.blob()).then(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `${numero}.pdf`; a.click();
      URL.revokeObjectURL(url);
    });
  };

  const envoyer = async (id) => {
    try {
      const r = await api.post(`/contrats-eta/v2/${id}/envoyer`, { message_personnalise: messageEnvoi });
      if (r.data.success) toast.success(`Email envoyé à ${r.data.destinataire}`);
      else toast.warning(`Envoi échoué. Lien signature : ${r.data.sign_url}`);
      setShowEnvoi(null); setMessageEnvoi(''); await charger();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur envoi'); }
  };

  const genererDevis = async (id) => {
    try { const r = await api.post(`/contrats-eta/v2/${id}/devis`); toast.success(`Devis ${r.data.devis.numero_devis} généré`); await charger(); }
    catch { toast.error('Erreur devis'); }
  };

  const addPrestation = () => setForm({ ...form, prestations: [...form.prestations, { categorie: 'viticulture', libelle: '', quantite: 1, unite: 'ha', prix_unitaire_ht: 0, remise_percent: 0 }] });
  const updatePrestation = (i, k, v) => { const np = [...form.prestations]; np[i] = { ...np[i], [k]: v }; setForm({ ...form, prestations: np }); };
  const removePrestation = (i) => setForm({ ...form, prestations: form.prestations.filter((_, idx) => idx !== i) });

  const calcTotal = () => {
    let ht = 0;
    form.prestations.forEach(p => {
      const base = (+p.quantite || 0) * (+p.prix_unitaire_ht || 0);
      ht += p.remise_percent ? base * (1 - p.remise_percent / 100) : base;
    });
    return { ht, tva: ht * 0.2, ttc: ht * 1.2 };
  };
  const totaux = calcTotal();

  const pickClient = (c) => {
    setForm({
      ...form,
      client_id: c.id || c._id || '',
      client: {
        ...form.client,
        nom: c.nom || c.raison_sociale || c.name || '',
        prenom: c.prenom || '',
        siret: c.siret || '',
        adresse: c.adresse || c.address || '',
        code_postal: c.code_postal || '',
        ville: c.ville || c.commune || '',
        email: c.email || '',
        telephone: c.telephone || c.phone || '',
      },
    });
  };

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="page-contrats-eta-simple">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-emerald-800 flex items-center gap-2">
            <FileSignature className="h-7 w-7" />Contrats ETA
          </h1>
          <p className="text-sm text-gray-500">Générateur ludique : signature en ligne, devis auto, lien CRM.</p>
        </div>
        <Button data-testid="btn-nouveau-contrat" onClick={() => { setForm(nouveauFormulaire()); setEditId(null); setShowForm(true); }} className="bg-emerald-600 hover:bg-emerald-700">
          <Plus className="h-4 w-4 mr-2" />Nouveau contrat
        </Button>
      </div>

      {loading && <p className="text-gray-500">Chargement...</p>}
      {!loading && contrats.length === 0 && (
        <Card><CardContent className="py-10 text-center text-gray-500">Aucun contrat pour le moment.</CardContent></Card>
      )}

      <div className="space-y-3" data-testid="contrats-list">
        {contrats.map(c => {
          const badge = STATUT_BADGES[c.statut] || STATUT_BADGES.brouillon;
          const isV2 = c.version === 'v2';
          return (
            <Card key={c.id} className="border-l-4 border-l-emerald-500" data-testid={`contrat-${c.id}`}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold">{c.numero}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${badge.cls}`}>{badge.label}</span>
                      {c.devis_numero && <span className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700">Devis {c.devis_numero}</span>}
                      {!isV2 && <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600">v1</span>}
                    </div>
                    <p className="text-sm mt-1">
                      <strong>{c.client?.nom || c.client_nom}</strong>
                      {c.client?.prenom && ` (${c.client.prenom})`}
                      {c.client?.email && ` — ${c.client.email}`}
                    </p>
                    <p className="text-sm text-gray-500">
                      {c.titre} {c.totaux?.total_ttc && `— ${c.totaux.total_ttc.toFixed(2)} € TTC`}
                    </p>
                    {c.signature && (
                      <p className="text-xs text-emerald-700 mt-1 flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" />Signé par {c.signature.nom_signataire} le {new Date(c.signature.signe_le).toLocaleString('fr-FR')}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-1 flex-wrap">
                    {isV2 && (
                      <>
                        <Button data-testid={`btn-pdf-${c.id}`} size="sm" variant="outline" onClick={() => telecharger(c.id, c.numero)}>
                          <Download className="h-3 w-3 mr-1" />PDF
                        </Button>
                        {c.statut !== 'signe' && (
                          <Button data-testid={`btn-edit-${c.id}`} size="sm" variant="outline" onClick={() => editer(c)}>Modifier</Button>
                        )}
                        {c.statut !== 'signe' && (
                          <Button data-testid={`btn-envoyer-${c.id}`} size="sm" onClick={() => setShowEnvoi(c)} className="bg-emerald-600 hover:bg-emerald-700">
                            <Send className="h-3 w-3 mr-1" />Envoyer
                          </Button>
                        )}
                        {!c.devis_id && (
                          <Button data-testid={`btn-devis-${c.id}`} size="sm" variant="outline" onClick={() => genererDevis(c.id)}>Devis</Button>
                        )}
                        {c.statut !== 'signe' && (
                          <Button data-testid={`btn-supprimer-${c.id}`} size="sm" variant="ghost" onClick={() => supprimer(c.id)} className="text-red-600 hover:bg-red-50">
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="form-contrat">
          <DialogHeader><DialogTitle>{editId ? 'Modifier le contrat' : 'Nouveau contrat ETA'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Input data-testid="input-titre" placeholder="Titre (ex: Contrat prestations viticoles 2026)" value={form.titre} onChange={e => setForm({ ...form, titre: e.target.value })} />

            <div>
              <h3 className="font-bold mb-2">Client</h3>
              {clients.length > 0 && (
                <select
                  data-testid="select-client-crm"
                  className="w-full border rounded p-2 mb-2 text-sm"
                  onChange={e => { const c = clients.find(x => (x.id || x._id) === e.target.value); if (c) pickClient(c); }}
                  value={form.client_id || ''}
                >
                  <option value="">-- Choisir un client du CRM ou saisir manuellement --</option>
                  {clients.slice(0, 200).map(c => (
                    <option key={c.id || c._id} value={c.id || c._id}>
                      {c.nom || c.raison_sociale || c.name} {c.prenom && `(${c.prenom})`} {c.ville && `— ${c.ville}`}
                    </option>
                  ))}
                </select>
              )}
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid="input-client-nom" placeholder="Nom / Raison sociale *" value={form.client.nom} onChange={e => setForm({ ...form, client: { ...form.client, nom: e.target.value } })} />
                <Input placeholder="Prénom" value={form.client.prenom} onChange={e => setForm({ ...form, client: { ...form.client, prenom: e.target.value } })} />
                <Input placeholder="SIRET" value={form.client.siret} onChange={e => setForm({ ...form, client: { ...form.client, siret: e.target.value } })} />
                <Input data-testid="input-client-email" placeholder="Email *" type="email" value={form.client.email} onChange={e => setForm({ ...form, client: { ...form.client, email: e.target.value } })} />
                <Input placeholder="Téléphone" value={form.client.telephone} onChange={e => setForm({ ...form, client: { ...form.client, telephone: e.target.value } })} />
                <Input placeholder="Adresse" value={form.client.adresse} onChange={e => setForm({ ...form, client: { ...form.client, adresse: e.target.value } })} />
                <Input placeholder="Code postal" value={form.client.code_postal} onChange={e => setForm({ ...form, client: { ...form.client, code_postal: e.target.value } })} />
                <Input placeholder="Ville" value={form.client.ville} onChange={e => setForm({ ...form, client: { ...form.client, ville: e.target.value } })} />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold">Prestations</h3>
                <Button data-testid="btn-ajouter-prestation" size="sm" onClick={addPrestation} variant="outline"><Plus className="h-3 w-3 mr-1" />Ajouter</Button>
              </div>
              <div className="space-y-2">
                {form.prestations.map((p, i) => (
                  <div key={i} className="grid grid-cols-12 gap-1 items-start border rounded p-2 bg-gray-50" data-testid={`prestation-${i}`}>
                    <select className="col-span-2 border rounded p-1 text-xs" value={p.categorie} onChange={e => updatePrestation(i, 'categorie', e.target.value)}>
                      {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <Input className="col-span-4" placeholder="Libellé (ex: Griffonnage 2 passages)" value={p.libelle} onChange={e => updatePrestation(i, 'libelle', e.target.value)} />
                    <Input className="col-span-1" type="number" step="0.1" placeholder="Qté" value={p.quantite} onChange={e => updatePrestation(i, 'quantite', +e.target.value)} />
                    <select className="col-span-1 border rounded p-1 text-xs" value={p.unite} onChange={e => updatePrestation(i, 'unite', e.target.value)}>
                      {UNITES.map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                    <Input className="col-span-2" type="number" step="0.01" placeholder="P.U. HT" value={p.prix_unitaire_ht} onChange={e => updatePrestation(i, 'prix_unitaire_ht', +e.target.value)} />
                    <Input className="col-span-1" type="number" placeholder="% rem" value={p.remise_percent} onChange={e => updatePrestation(i, 'remise_percent', +e.target.value)} />
                    <Button size="sm" variant="ghost" className="col-span-1 text-red-600" onClick={() => removePrestation(i)}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                ))}
              </div>
              <div className="text-right mt-2 text-sm">
                HT: <b>{totaux.ht.toFixed(2)} €</b> &middot; TVA: {totaux.tva.toFixed(2)} € &middot; <span className="text-emerald-700 font-bold">TTC: {totaux.ttc.toFixed(2)} €</span>
              </div>
            </div>

            <div>
              <h3 className="font-bold mb-2">Conditions</h3>
              <div className="grid grid-cols-2 gap-2">
                <select className="border rounded p-2 text-sm" value={form.conditions.mode_facturation} onChange={e => setForm({ ...form, conditions: { ...form.conditions, mode_facturation: e.target.value } })}>
                  <option value="intervention">À l'intervention</option>
                  <option value="mensuel">Mensuel</option>
                  <option value="acompte_solde">Acompte + solde</option>
                </select>
                <select className="border rounded p-2 text-sm" value={form.conditions.mode_paiement} onChange={e => setForm({ ...form, conditions: { ...form.conditions, mode_paiement: e.target.value } })}>
                  <option value="virement">Virement</option>
                  <option value="cheque">Chèque</option>
                  <option value="especes">Espèces</option>
                  <option value="prelevement">Prélèvement</option>
                </select>
                <Input placeholder="Délai paiement (jours)" type="number" value={form.conditions.delai_paiement_jours} onChange={e => setForm({ ...form, conditions: { ...form.conditions, delai_paiement_jours: +e.target.value } })} />
                <Input placeholder="Acompte %" type="number" value={form.conditions.acompte_percent} onChange={e => setForm({ ...form, conditions: { ...form.conditions, acompte_percent: +e.target.value } })} />
                <Input placeholder="Date début" type="date" value={form.conditions.date_debut} onChange={e => setForm({ ...form, conditions: { ...form.conditions, date_debut: e.target.value } })} />
                <Input placeholder="Date fin" type="date" value={form.conditions.date_fin} onChange={e => setForm({ ...form, conditions: { ...form.conditions, date_fin: e.target.value } })} />
                <Input className="col-span-2" placeholder="Lieu d'exécution" value={form.conditions.lieu_execution} onChange={e => setForm({ ...form, conditions: { ...form.conditions, lieu_execution: e.target.value } })} />
              </div>
            </div>

            <Textarea data-testid="textarea-notes-internes" placeholder="Notes internes (invisibles client)" value={form.notes_internes} onChange={e => setForm({ ...form, notes_internes: e.target.value })} rows={2} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Annuler</Button>
            <Button data-testid="btn-submit-contrat" onClick={submit} className="bg-emerald-600 hover:bg-emerald-700">{editId ? 'Enregistrer' : 'Créer'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!showEnvoi} onOpenChange={() => setShowEnvoi(null)}>
        <DialogContent data-testid="dialog-envoi">
          <DialogHeader><DialogTitle>Envoyer le contrat au client</DialogTitle></DialogHeader>
          {showEnvoi && (
            <div className="space-y-3">
              <p className="text-sm"><b>Destinataire :</b> {showEnvoi.client?.email || <span className="text-red-600">Email manquant !</span>}</p>
              <Textarea data-testid="textarea-message-envoi" placeholder="Message personnalisé (optionnel)" value={messageEnvoi} onChange={e => setMessageEnvoi(e.target.value)} rows={4} />
              <p className="text-xs text-gray-500">Le client recevra un email avec un lien unique pour consulter et signer en ligne.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEnvoi(null)}>Annuler</Button>
            <Button data-testid="btn-confirmer-envoi" onClick={() => envoyer(showEnvoi.id)} className="bg-emerald-600 hover:bg-emerald-700">
              <Send className="h-3 w-3 mr-2" />Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
